import {AddBetslipSelection, OddsChangeFlag} from '@/features/betslip/betslip-create/lib/models/betslip-selection'
import {InMatchResultDto} from '@/api/player-api/models/in-match-result-dto'

/**
 * The betslip-demo offer (`betslip-demo/server/offer.mjs`): 17 matches, 33 selections — the
 * demo's 34 less its Asian Handicap.
 *
 * Fixed rather than live, so a case built in the workbench can be rebuilt tomorrow and matches
 * the vectors recorded in the demo. The ids are the numeric ones the demo records vectors with
 * (`m16` → 16, `o31` → 31, markets by `MARKET_IDS`), so a vector means the same thing in both.
 *
 * Several matches carry more than one selection so the Betslip can become a Bet Builder, and
 * the last two carry odds of 25 and 17 — the size that makes a limit bite on a short Betslip.
 */

/** A market-outcome pair, the unit a Liability Limit is configured against. */
export interface MarketOutcome {
	marketId: number
	/** The same in every fixture — `home` is "Arsenal" on one match and "PSG" on another. */
	outcomeKey: string
	marketLabel: string
	outcomeLabel: string
}

export interface OfferSelection extends AddBetslipSelection {
	outcomeKey: string
	homeName: string
	awayName: string
	league: string
}

interface Match {
	matchId: number
	home: string
	away: string
	league: string
	isLiveMatch: boolean
}

const MATCHES: Match[] = [
	{matchId: 1, home: 'Arsenal', away: 'Tottenham', league: 'Premier League', isLiveMatch: false},
	{matchId: 2, home: 'Real Madrid', away: 'Barcelona', league: 'LaLiga', isLiveMatch: true},
	{matchId: 3, home: 'Bayern Munich', away: 'Borussia Dortmund', league: 'Bundesliga', isLiveMatch: false},
	{matchId: 4, home: 'Inter', away: 'AC Milan', league: 'Serie A', isLiveMatch: true},
	{matchId: 5, home: 'PSG', away: 'Marseille', league: 'Ligue 1', isLiveMatch: false},
	{matchId: 6, home: 'Ajax', away: 'PSV', league: 'Eredivisie', isLiveMatch: false},
	{matchId: 7, home: 'Liverpool', away: 'Manchester City', league: 'Premier League', isLiveMatch: false},
	{matchId: 8, home: 'Chelsea', away: 'Manchester United', league: 'Premier League', isLiveMatch: true},
	{matchId: 9, home: 'Juventus', away: 'Napoli', league: 'Serie A', isLiveMatch: false},
	{matchId: 10, home: 'Atletico Madrid', away: 'Sevilla', league: 'LaLiga', isLiveMatch: false},
	{matchId: 11, home: 'Porto', away: 'Benfica', league: 'Primeira Liga', isLiveMatch: true},
	{matchId: 12, home: 'Celtic', away: 'Rangers', league: 'Scottish Premiership', isLiveMatch: false},
	{matchId: 13, home: 'Galatasaray', away: 'Fenerbahce', league: 'Super Lig', isLiveMatch: false},
	{matchId: 14, home: 'Roma', away: 'Lazio', league: 'Serie A', isLiveMatch: false},
	{matchId: 15, home: 'Feyenoord', away: 'AZ Alkmaar', league: 'Eredivisie', isLiveMatch: false},
	{matchId: 16, home: 'Portugal', away: 'Luxembourg', league: 'European Qualifiers', isLiveMatch: false},
	{matchId: 17, home: 'Croatia', away: 'Latvia', league: 'European Qualifiers', isLiveMatch: false}
]

const OVER_UNDER: [string, string][] = [['over', 'Over'], ['under', 'Under']]

/** Numeric ids follow the demo's `MARKET_IDS`: mk-1x2 → 1 … 6. Its Asian Handicap (mk-ah → 7) is left out. */
const MARKETS: {marketId: number, marketLabel: string, outcomes: [string, string][]}[] = [
	{marketId: 1, marketLabel: 'Full Time Result', outcomes: [['home', 'Home'], ['draw', 'Draw'], ['away', 'Away']]},
	{marketId: 2, marketLabel: 'Total Goals 1.5', outcomes: OVER_UNDER},
	{marketId: 3, marketLabel: 'Total Goals 2.5', outcomes: OVER_UNDER},
	{marketId: 4, marketLabel: 'Total Goals 3.5', outcomes: OVER_UNDER},
	{marketId: 5, marketLabel: 'Both Teams To Score', outcomes: [['yes', 'Yes'], ['no', 'No']]},
	{
		marketId: 6,
		marketLabel: 'Double Chance',
		outcomes: [['1x', 'Home or Draw'], ['12', 'Home or Away'], ['x2', 'Draw or Away']]
	}
]

/**
 * Total Goals 1.5, 2.5 and 3.5 are lines of one market, as on the feed, where a Selection does not
 * carry its line. Their Liability Limit is set once for the market, whatever the line.
 */
const TOTAL_GOALS_LINES = [2, 3, 4]
const TOTAL_GOALS_LABEL = 'Total Goals'

/** [oddsId, matchId, marketId, outcomeKey, outcomeLabel, odds]. 12 was the demo's Asian Handicap. */
const OUTCOMES: [number, number, number, string, string, number][] = [
	[1, 1, 1, 'home', 'Arsenal', 2.1],
	[2, 1, 1, 'draw', 'Draw', 3.4],
	[3, 1, 3, 'over', 'Over', 1.85],
	[4, 1, 5, 'yes', 'Yes', 1.7],

	[5, 2, 1, 'home', 'Real Madrid', 2.45],
	[6, 2, 4, 'over', 'Over', 2.6],
	[7, 2, 6, '1x', 'Real Madrid or Draw', 1.35],

	[8, 3, 1, 'home', 'Bayern Munich', 1.55],
	[9, 3, 5, 'yes', 'Yes', 1.6],

	[10, 4, 1, 'away', 'AC Milan', 3.1],
	[11, 4, 3, 'under', 'Under', 1.95],

	[13, 5, 1, 'home', 'PSG', 1.4],
	[14, 5, 3, 'over', 'Over', 1.75],

	[15, 6, 1, 'draw', 'Draw', 3.6],

	[16, 7, 1, 'away', 'Manchester City', 2.25],
	[17, 7, 5, 'yes', 'Yes', 1.55],
	[18, 7, 4, 'over', 'Over', 2.4],

	[19, 8, 1, 'home', 'Chelsea', 2.05],
	[20, 8, 6, 'x2', 'Draw or Manchester United', 1.62],

	[21, 9, 1, 'home', 'Juventus', 2.15],
	[22, 9, 3, 'under', 'Under', 1.8],

	[23, 10, 1, 'home', 'Atletico Madrid', 1.68],

	[24, 11, 1, 'draw', 'Draw', 3.25],
	[25, 11, 2, 'over', 'Over', 1.3],

	[26, 12, 1, 'home', 'Celtic', 1.95],
	[27, 12, 5, 'no', 'No', 2.1],

	[28, 13, 1, 'away', 'Fenerbahce', 2.7],

	[29, 14, 1, 'home', 'Roma', 2.3],

	[30, 15, 3, 'over', 'Over', 1.66],

	[31, 16, 1, 'away', 'Luxembourg', 25],
	[32, 16, 4, 'over', 'Over', 1.45],

	[33, 17, 1, 'away', 'Latvia', 17],
	[34, 17, 5, 'yes', 'Yes', 2.9]
]

const matchesById = new Map(MATCHES.map((match) => [match.matchId, match]))
const marketsById = new Map(MARKETS.map((market) => [market.marketId, market]))

export const DEMO_OFFER: OfferSelection[] = OUTCOMES.map(([oddsId, matchId, marketId, outcomeKey, outcomeLabel, odds]) => {
	const match = matchesById.get(matchId) as Match

	return {
		matchId,
		matchName: `${match.home} - ${match.away}`,
		homeName: match.home,
		awayName: match.away,
		league: match.league,
		isLiveMatch: match.isLiveMatch,
		marketId,
		marketLabel: marketsById.get(marketId)?.marketLabel ?? '',
		oddsId,
		outcomeKey,
		outcomeLabel,
		odds,
		oddsFormatted: odds.toFixed(2),
		oddsChangeFlag: OddsChangeFlag.none,
		matchResult: {} as InMatchResultDto,
		isPlayable: true,
		marketStatus: 0,
		outcomeStatus: 0,
		isBlocked: false
	}
})

/** The offer grouped by match, in offer order. */
export const DEMO_OFFER_BY_MATCH: OfferSelection[][] = MATCHES
	.map((match) => DEMO_OFFER.filter((selection) => selection.matchId === match.matchId))
	.filter((selections) => selections.length > 0)

/**
 * Every market-outcome pair the offer's markets define — including pairs no fixture offers
 * today, because the limit belongs to the pair, not to today's fixtures. The Total Goals lines
 * give one pair per outcome.
 */
export const MARKET_OUTCOMES: MarketOutcome[] = MARKETS.flatMap((market) =>
	market.outcomes.map(([outcomeKey, outcomeLabel]) => ({
		marketId: market.marketId,
		outcomeKey,
		marketLabel: isTotalGoalsLine(market.marketId) ? TOTAL_GOALS_LABEL : market.marketLabel,
		outcomeLabel
	})))
	.filter((pair, index, pairs) => pairs.findIndex((other) => pairKey(other) === pairKey(pair)) === index)

export function offerSelection(oddsId: number): OfferSelection | undefined {
	return DEMO_OFFER.find((selection) => selection.oddsId === oddsId)
}

/** The pair a Liability Limit is looked up by; every Total Goals line shares its market's. */
export function pairKey(pair: {marketId: number, outcomeKey: string}): string {
	const market = isTotalGoalsLine(pair.marketId) ? 'total-goals' : pair.marketId
	return `${market}:${pair.outcomeKey}`
}

function isTotalGoalsLine(marketId: number): boolean {
	return TOTAL_GOALS_LINES.includes(marketId)
}
